import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Minus } from "lucide-react";

export default function ReductionModal({ kids, defaultKidId, onClose }) {
  const [selected, setSelected] = useState(defaultKidId ? [defaultKidId] : []);
  const [minutes, setMinutes] = useState(5);
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("");
  const [cats, setCats] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/reduction-categories").then((r) => setCats(r.data)); }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return;
    setSubmitting(true);
    await api.post("/reductions", { kid_ids: selected, minutes, reason, category });
    setSubmitting(false);
    onClose?.(true);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" data-testid="reduction-modal">
      <form onSubmit={submit} className="card-soft p-8 w-full max-w-lg fade-in-up">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#C06E52]/10 text-[#C06E52]">
            <Minus size={20} />
          </span>
          <h3 className="font-heading text-2xl font-semibold text-[#2C342A]">Time reduction</h3>
        </div>
        <p className="text-[#707A6E] text-sm mb-6">Deduct minutes from one or more kids' weekly pool.</p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Kids</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {kids.map((k) => (
                <button
                  type="button" key={k.id}
                  onClick={() => toggle(k.id)}
                  data-testid={`reduction-kid-${k.id}`}
                  className={`rounded-xl px-3 py-2 text-sm border transition-colors ${
                    selected.includes(k.id) ? "bg-[#5C7A4F] text-white border-[#5C7A4F]" : "bg-white text-[#2C342A] border-[#E4E2DC] hover:bg-[#F0EFEB]"
                  }`}
                >
                  {k.name}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Minutes</label>
              <input type="number" min="1" max="180" value={minutes}
                     onChange={(e) => setMinutes(parseInt(e.target.value) || 5)}
                     className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" data-testid="reduction-minutes" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" data-testid="reduction-category">
                <option value="">— pick one —</option>
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Reason (free text)</label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="What happened?" className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" data-testid="reduction-reason" />
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button type="button" onClick={() => onClose?.(false)} className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 font-medium" data-testid="reduction-cancel">
            Cancel
          </button>
          <button type="submit" disabled={submitting || selected.length === 0}
                  className="flex-1 rounded-xl bg-[#C06E52] text-white px-4 py-3 font-medium hover:bg-[#A35A42] disabled:opacity-50" data-testid="reduction-apply">
            {submitting ? "Applying…" : `Apply -${minutes} min`}
          </button>
        </div>
      </form>
    </div>
  );
}
